import URLS from "~/constants/urls"

const FILES_HOST = /^files(-dev|-stag)?\.orbitshift\.ai$/

export function isFilesHost(url: string | null | undefined): boolean {
  if (!url) return false
  try {
    return FILES_HOST.test(new URL(url).hostname)
  } catch {
    return false
  }
}

export function needsSignedPreview(url: string | null | undefined): boolean {
  if (!isFilesHost(url)) return false
  try {
    return !new URL(url as string).searchParams.has("Signature")
  } catch {
    return false
  }
}

export function uploadMeta(file: { type?: string; size: number }) {
  return { type: file.type || "application/octet-stream", size: file.size }
}

export function withFileCredentials<T extends RequestInit>(
  url: string,
  init?: T
): T {
  const base = (init ?? {}) as T
  return isFilesHost(url) ? { ...base, credentials: "include" } : base
}

export function fileAxiosConfig(url: string): { withCredentials?: boolean } {
  return isFilesHost(url) ? { withCredentials: true } : {}
}

type SessionCtx = {
  token: string
  tenantId: number
  userId: number
  linkedTenantId?: number | null
}
type Deps = { fetch?: typeof fetch; base?: string }

const sessionUrl = (base: string, c: SessionCtx) =>
  `${base}/tenants/${c.tenantId}/users/${c.userId}/files/session`

export async function issueFilesSession(c: SessionCtx, deps: Deps = {}) {
  const f = deps.fetch ?? fetch
  const url = sessionUrl(deps.base ?? URLS.V1, c)
  const targets = [url]
  if (c.linkedTenantId && c.linkedTenantId !== c.tenantId)
    targets.push(`${url}?linked_tenant_id=${c.linkedTenantId}`)
  await Promise.all(
    targets.map((u) =>
      f(u, {
        credentials: "include",
        headers: { Authorization: `Bearer ${c.token}` }
      }).then(
        () => undefined,
        () => undefined
      )
    )
  )
}

export async function clearFilesSession(c: SessionCtx, deps: Deps = {}) {
  const f = deps.fetch ?? fetch
  const url = sessionUrl(deps.base ?? URLS.V1, c)
  const targets = [url]
  if (c.linkedTenantId && c.linkedTenantId !== c.tenantId)
    targets.push(`${url}?linked_tenant_id=${c.linkedTenantId}`)
  await Promise.all(
    targets.map((u) =>
      f(u, {
        method: "DELETE",
        credentials: "include",
        headers: { Authorization: `Bearer ${c.token}` }
      }).then(
        () => undefined,
        () => undefined
      )
    )
  )
}

export function installFilesImageRetry(
  refresh: () => Promise<void>,
  doc: Document = document
) {
  let inflight: Promise<void> | null = null
  const onError = (e: Event | { target: unknown }) => {
    const el = e.target as HTMLImageElement | null
    if (
      !el ||
      el.tagName !== "IMG" ||
      !isFilesHost(el.src) ||
      el.dataset.filesRetried
    )
      return
    el.dataset.filesRetried = "1"
    inflight ??= refresh().finally(() => {
      inflight = null
    })
    void inflight.then(() => {
      const src = el.src
      el.src = ""
      el.src = src
    })
  }
  doc.addEventListener("error", onError as EventListener, true)
  return () => doc.removeEventListener("error", onError as EventListener, true)
}
